import { ReportingCompanyService } from '@http/clients/reporting-app/reporting-company.service';
import { TabNames } from '@models/enums/TabNames';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { WidgetType } from '@pages/reports/types/report-widget-type.enum';
import { formatNumbersCompact, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import {
    accountEditorConfig,
    companyTypeEditorConfig,
    customerEditorConfig,
    departmentEditorConfig,
    includeArchivedEditorConfig,
    includeOpportunityArchivedEditorConfig,
    marketsEditorConfig,
    opportunityDueDateEditorConfig,
    opportunitySourceEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    salesPersonsEditorConfig
} from './shared/additional-filters';
import { crmMainFilters } from './shared/main-filters';

export const Companies: IReportConfiguration<ReportingCompanyService> = {
    base: {
        type: ReportType.Companies,
        serviceType: ReportingCompanyService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getCompanyBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getCompanyMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Average' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [{ name: 'AwardedAmount', label: 'Awarded Amount', format: 'currency' }]
                }
            },
            {
                methodName: 'getCompanyLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Companies by Awarded Amount'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'companyName',
                            yKey: 'awardedAmount',
                            yName: 'Awarded Amount'
                        },
                        {
                            type: 'line',
                            xKey: 'companyName',
                            yKey: 'awardedCount',
                            yName: 'Awarded Count'
                        },
                        {
                            type: 'line',
                            xKey: 'companyName',
                            yKey: 'opportunityQty',
                            yName: 'Opportunities Count'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['awardedAmount'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Quantity'
                            },
                            keys: ['awardedCount', 'opportunityQty'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getCompanyColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'awardedAmount',
                            yName: 'Awarded Amount'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['awardedAmount'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.ExtendedTwoPlusOne
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getCompanyValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'entityId',
                        headerName: 'Company ID',
                        lockVisible: true,
                        enableRowGroup: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (params.value) {
                                const link = `/companies-single/${params.value}/${TabNames[TabNames.Overview]}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                            return `<div></div>`;
                        }
                    },
                    { field: 'companyName', headerName: 'Company Name', enableRowGroup: true, lockVisible: true },
                    { field: 'account', headerName: 'Account', enableRowGroup: true },
                    { field: 'mainOfficeFullAddress', headerName: 'Main Office Full Address' },
                    { field: 'mainContact', headerName: 'Main Contact', enableRowGroup: true },
                    { field: 'companyType', headerName: 'Company Type', enableRowGroup: true },
                    { field: 'market', headerName: 'Company Market' },
                    { field: 'opportunityQty', headerName: 'Opportunity Qty (Customer)', aggFunc: 'sum' },
                    { field: 'salesPersons', headerName: 'Salespersons', enableRowGroup: true },
                    { field: 'awardedCount', headerName: 'Awarded Count', aggFunc: 'sum' },
                    { field: 'awardedAmount', headerName: 'Awarded Amount', type: 'currency', aggFunc: 'sum' },
                    { field: 'organization', headerName: 'Organization', enableRowGroup: true },
                    { field: 'condition', headerName: 'Condition', enableRowGroup: true },
                    {
                        field: 'companyLastUpdatedNote',
                        headerName: 'Last Updated Note',
                        tooltipField: 'companyLastUpdatedNote'
                    },
                    { field: 'modificationDate', headerName: 'Modification Date', type: 'date' }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...crmMainFilters],
        additional: [
            opportunityDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig,
            departmentEditorConfig,
            customerEditorConfig,
            companyTypeEditorConfig,
            accountEditorConfig,
            salesPersonsEditorConfig,
            includeOpportunityArchivedEditorConfig,
            includeArchivedEditorConfig
        ]
    }
};
